/**
 * GuestBanner — surfaced when the visitor has completed value-first
 * onboarding but not yet created an account.
 *
 * Two controls:
 *   • "Create Account to Save" (primary) → `/login?from=guest-workspace`
 *   • "Exit preview" (× icon)           → clears guest session + draft
 *                                          → routes to `/welcome`
 *
 * The × button used to only hide the banner in-memory, which meant a
 * refresh silently brought the visitor right back into a stranger's
 * personalized workspace. Now it fully ends the preview.
 */
import { Sparkles, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/entities/auth/store";

export function GuestBanner(): JSX.Element | null {
  const token = useAuthStore((s) => s.accessToken);
  const guest = useAuthStore((s) => s.guest);
  const endGuestPreview = useAuthStore((s) => s.endGuestPreview);
  const navigate = useNavigate();

  if (token) return null;
  if (!guest.active) return null;

  const name = guest.businessName?.trim() || "your brand";

  const handleExit = () => {
    endGuestPreview();
    navigate("/welcome", { replace: true });
  };

  return (
    <div
      data-testid="guest-banner"
      className="sticky top-0 z-30 flex items-center gap-3 border-b border-[color:var(--md-amber)]/40 px-4 py-2.5 text-sm backdrop-blur-md"
      style={{
        background:
          "linear-gradient(90deg, rgba(229,180,84,0.14) 0%, rgba(229,180,84,0.06) 40%, transparent 100%)",
      }}
    >
      <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-[var(--md-amber)]/20 text-[var(--md-amber)]">
        <Sparkles className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--md-amber)]">
          Your Orkexra workspace is ready
        </div>
        <div className="mt-0.5 truncate text-[12px] text-[var(--md-ivory-0)]">
          <span className="text-[var(--md-fog)]">
            Create your account to save this Brand Brain, unlock campaign
            generation, and keep building your marketing engine for{" "}
          </span>
          <b className="text-[var(--md-ivory-0)]">{name}</b>.
        </div>
      </div>
      <Link
        to="/login?from=guest-workspace"
        data-testid="guest-banner-cta"
        className="md-btn md-btn--primary whitespace-nowrap"
        style={{ padding: "6px 14px", fontSize: 12 }}
      >
        Create Account to Save
      </Link>
      <button
        type="button"
        onClick={handleExit}
        data-testid="guest-banner-exit"
        className="hidden shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-mono uppercase tracking-widest text-[var(--md-fog)] transition hover:bg-[var(--md-ink-2)] hover:text-[var(--md-ivory-0)] md:inline-flex"
        aria-label="Exit guest preview and clear this workspace"
        title="Exit preview and clear this workspace"
      >
        Exit preview
      </button>
      <button
        type="button"
        onClick={handleExit}
        data-testid="guest-banner-dismiss"
        className="rounded-full p-1 text-[var(--md-fog)] transition hover:bg-[var(--md-ink-2)] hover:text-[var(--md-ivory-0)]"
        aria-label="Exit guest preview and clear this workspace"
        title="Exit preview and clear this workspace"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
